import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { 
  Truck, Package, Clock, CheckCircle, MapPin, Phone, Building2,
  ChevronDown, ChevronUp, FileText, Camera, Navigation, User, X, Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import ImageLightbox from '@/components/ImageLightbox';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PICKUP_STATUSES = [
  { value: "assigned", label: "Assigned", color: "bg-blue-500/20 text-blue-400", icon: Clock },
  { value: "on_the_way", label: "On the Way", color: "bg-orange-500/20 text-orange-400", icon: Navigation },
  { value: "reached_vendor", label: "At Vendor", color: "bg-purple-500/20 text-purple-400", icon: MapPin },
  { value: "picked", label: "Picked", color: "bg-green-500/20 text-green-400", icon: Package },
  { value: "delivered_to_warehouse", label: "Delivered", color: "bg-teal-500/20 text-teal-400", icon: Building2 },
  { value: "completed", label: "Completed", color: "bg-gray-500/20 text-gray-400", icon: CheckCircle }
];

const VendorPickupsPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({ total: 0, assigned: 0, in_progress: 0, picked: 0, delivered: 0 });
  const [expandedTasks, setExpandedTasks] = useState({});
  const [selectedTask, setSelectedTask] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingTaskId, setUpdatingTaskId] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalMatching, setTotalMatching] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 50;
  
  // POD Dialog states (only for "picked" status)
  const [podDialogOpen, setPodDialogOpen] = useState(false);
  const [podFiles, setPodFiles] = useState([]);
  const [uploadingPod, setUploadingPod] = useState(false);
  const podInputRef = useRef(null);
  const podGalleryInputRef = useRef(null);
  
  // Partial pickup - track picked quantities per line item
  const [pickedItems, setPickedItems] = useState([]);
  
  // Re-pickup state
  const [initiatingRepickup, setInitiatingRepickup] = useState(null);
  const [isRepickupMode, setIsRepickupMode] = useState(false);

  const isDriver = user?.roles?.includes('driver');

  useEffect(() => {
    loadPickupTasks(1);
  }, [statusFilter]);

  const loadPickupTasks = async (targetPage = 1) => {
    const appending = targetPage > 1;
    if (appending) setLoadingMore(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (isDriver) {
        params.append('driver_id', user.id);
      }
      params.append('status', statusFilter);
      params.append('page', String(targetPage));
      params.append('page_size', String(PAGE_SIZE));
      
      const response = await axios.get(`${API_BASE_URL}/enterprise/driver/pickup-tasks?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newTasks = response.data.tasks || [];
      setTasks(prev => (appending ? [...prev, ...newTasks] : newTasks));
      setSummary(response.data.summary || { total: 0, assigned: 0, in_progress: 0, picked: 0, delivered: 0 });
      setPage(targetPage);
      setHasMore(!!response.data.has_more);
      setTotalMatching(response.data.total_matching ?? newTasks.length);
    } catch (error) {
      toast.error('Failed to load pickup tasks');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const getStatusBadge = (status) => {
    const config = PICKUP_STATUSES.find(s => s.value === status);
    if (!config) return null;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border-0 flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getNextStatus = (currentStatus) => {
    // Driver workflow ends at "delivered_to_warehouse" - "completed" is set via GRN by office staff
    const statusFlow = ['assigned', 'on_the_way', 'reached_vendor', 'picked', 'delivered_to_warehouse'];
    const currentIndex = statusFlow.indexOf(currentStatus);
    return currentIndex < statusFlow.length - 1 ? statusFlow[currentIndex + 1] : null;
  };

  const getStatusActionLabel = (status) => {
    switch(status) {
      case 'on_the_way': return 'Start Trip';
      case 'reached_vendor': return 'Reached Vendor';
      case 'picked': return 'Mark Picked';
      case 'delivered_to_warehouse': return 'Mark Delivered';
      default: return 'Update';
    }
  };

  const getStatusButtonColor = (status) => {
    switch(status) {
      case 'on_the_way': return 'bg-orange-500 hover:bg-orange-600';
      case 'reached_vendor': return 'bg-purple-500 hover:bg-purple-600';
      case 'picked': return 'bg-green-500 hover:bg-green-600';
      case 'delivered_to_warehouse': return 'bg-teal-500 hover:bg-teal-600';
      default: return 'bg-wt-gold hover:bg-wt-gold-hover';
    }
  };

  // Check if a task has remaining items to pick
  const hasRemainingItems = (task) => {
    if (!task.line_items) return false;
    return task.line_items.some(item => {
      const ordered = item.quantity || 0;
      const picked = item.picked_quantity || 0;
      return picked < ordered;
    });
  };

  // Get count of remaining items
  const getRemainingItemsCount = (task) => {
    if (!task.line_items) return 0;
    return task.line_items.reduce((total, item) => {
      const ordered = item.quantity || 0;
      const picked = item.picked_quantity || 0;
      return total + Math.max(0, ordered - picked);
    }, 0);
  };

  // Initiate re-pickup for remaining items
  const handleInitiateRepickup = async (task, e) => {
    e.stopPropagation();
    setInitiatingRepickup(task.id);
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/enterprise/purchase-orders/${task.id}/initiate-repickup`,
        {
          updated_by: user?.id,
          updated_by_name: user?.full_name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Re-pickup initiated! You can now pick the remaining items.');
      loadPickupTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate re-pickup');
    } finally {
      setInitiatingRepickup(null);
    }
  };

  // Direct status update (no dialog needed)
  const handleDirectStatusUpdate = async (task, newStatus) => {
    setUpdatingTaskId(task.id);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/enterprise/purchase-orders/${task.id}/pickup-status`,
        { 
          status: newStatus,
          updated_by: user?.id,
          updated_by_name: user?.full_name
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Status updated to ${PICKUP_STATUSES.find(s => s.value === newStatus)?.label}`);
      loadPickupTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUpdatingTaskId(null);
    }
  };

  // Open POD dialog for "picked" status
  const openPodDialog = (task, repickupMode = false) => {
    setSelectedTask(task);
    setPodFiles([]);
    setIsRepickupMode(repickupMode);
    
    // Initialize picked items
    if (task.line_items) {
      const itemsForPickup = task.line_items
        .map(item => {
          const previouslyPicked = item.picked_quantity || 0;
          const remainingToPick = item.quantity - previouslyPicked;
          return {
            line_id: item.id,
            sku: item.sku,
            product_title: item.product_title,
            ordered_quantity: item.quantity,
            previously_picked: previouslyPicked,
            remaining_quantity: remainingToPick,
            picked_quantity: remainingToPick,
            image_url: item.image_url
          };
        })
        .filter(item => item.remaining_quantity > 0);
      setPickedItems(itemsForPickup);
    }
    
    setPodDialogOpen(true);
  };

  // Handle action button click
  const handleActionClick = (task, e) => {
    e.stopPropagation();
    const nextStatus = getNextStatus(task.pickup_status);
    if (!nextStatus) return;
    
    // Only show dialog for "picked" status (requires POD)
    if (nextStatus === 'picked') {
      openPodDialog(task);
    } else {
      handleDirectStatusUpdate(task, nextStatus);
    }
  };

  // Handle POD submission
  const handlePodSubmit = async () => {
    if (!selectedTask) return;
    
    if (!podFiles.length && (!selectedTask.pickup_pod_images || selectedTask.pickup_pod_images.length === 0)) {
      toast.error('Please upload a Proof of Pickup (POD) image');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      let podImages = selectedTask.pickup_pod_images || [];
      
      if (podFiles.length) {
        setUploadingPod(true);
        for (const f of podFiles) {
          const formData = new FormData();
          formData.append('file', f);
          const uploadRes = await axios.post(
            `${API_BASE_URL}/enterprise/purchase-orders/${selectedTask.id}/pickup-pod`,
            formData,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
          );
          podImages = [...podImages, uploadRes.data.file_url];
        }
      }
      
      const requestData = { 
        status: 'picked',
        pod_images: podImages,
        picked_items: pickedItems.map(item => ({
          line_id: item.line_id,
          picked_quantity: item.picked_quantity
        })),
        updated_by: user?.id,
        updated_by_name: user?.full_name
      };
      
      await axios.put(
        `${API_BASE_URL}/enterprise/purchase-orders/${selectedTask.id}/pickup-status`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Marked as Picked');
      setPodDialogOpen(false);
      setPodFiles([]);
      setPickedItems([]);
      loadPickupTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setUploadingPod(false);
    }
  };

  const handlePodFileSelect = (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
    if (files.length < (e.target.files?.length || 0)) {
      toast.error('Only image files are allowed');
    }
    if (files.length) {
      setPodFiles(prev => [...prev, ...files].slice(0, 6));
    }
    e.target.value = '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="vendor-pickups-page">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold font-playfair text-wt-text-primary">
            Vendor Pickups
          </h1>
          <p className="text-wt-text-muted text-sm">
            {isDriver ? 'Your pickup tasks' : 'All pickup tasks'}
          </p>
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48 bg-wt-paper border-wt-border text-wt-text-primary">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent className="bg-wt-paper border-wt-border">
            <SelectItem value="all">All Tasks</SelectItem>
            <SelectItem value="active">Active Only</SelectItem>
            {PICKUP_STATUSES.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Stats - Mobile Optimized */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2 md:gap-4">
        {[
          { label: 'Total', value: summary.total, color: 'text-wt-gold' },
          { label: 'Assigned', value: summary.assigned, color: 'text-blue-400' },
          { label: 'In Progress', value: summary.in_progress, color: 'text-orange-400' },
          { label: 'Picked', value: summary.picked, color: 'text-green-400' },
          { label: 'Delivered', value: summary.delivered, color: 'text-teal-400' }
        ].map((stat, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-wt-paper border border-wt-border rounded-lg p-3 text-center"
          >
            <p className={`text-xl md:text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-xs text-wt-text-muted">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Task Cards */}
      <div className="space-y-3">
        {tasks.length === 0 ? (
          <div className="bg-wt-paper border border-wt-border rounded-xl p-8 text-center">
            <Truck className="h-12 w-12 text-wt-text-muted mx-auto mb-3 opacity-50" />
            <h3 className="text-lg font-medium text-wt-text-primary">No Pickup Tasks</h3>
            <p className="text-wt-text-muted text-sm mt-1">
              {isDriver ? 'No tasks assigned to you' : 'No tasks found'}
            </p>
          </div>
        ) : (
          tasks.map((task, index) => {
            const nextStatus = getNextStatus(task.pickup_status);
            const isExpanded = expandedTasks[task.id];
            const isUpdating = updatingTaskId === task.id;

            return (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden"
                data-testid={`pickup-task-${task.id}`}
              >
                {/* Card Header */}
                <div 
                  className="p-4 cursor-pointer"
                  onClick={() => toggleTaskExpand(task.id)}
                >
                  {/* Top Row: PO Number + Status */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-wt-gold font-semibold text-sm">{task.po_number}</span>
                    {getStatusBadge(task.pickup_status)}
                  </div>
                  
                  {/* Vendor Info */}
                  <h3 className="text-wt-text-primary font-medium text-base mb-1">{task.vendor_name}</h3>
                  <p className="text-wt-text-muted text-sm line-clamp-1">{task.vendor_address}, {task.vendor_city}</p>
                  
                  {/* Stats Row */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-wt-border/50">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-wt-text-muted">
                        <Package className="h-4 w-4 inline mr-1" />
                        {task.total_quantity} pcs
                      </span>
                      {task.pickup_date && (
                        <span className="text-wt-text-muted">
                          {task.pickup_date}
                        </span>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-wt-text-muted" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-wt-text-muted" />
                    )}
                  </div>
                </div>

                {/* Action Button - Only show if not yet delivered (completed is via GRN) */}
                {!['delivered_to_warehouse', 'completed'].includes(task.pickup_status) && nextStatus && (
                  <div className="px-4 pb-4">
                    <Button
                      className={`w-full text-white ${getStatusButtonColor(nextStatus)}`}
                      onClick={(e) => handleActionClick(task, e)}
                      disabled={isUpdating}
                      data-testid={`action-btn-${task.id}`}
                    >
                      {isUpdating ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Updating...
                        </span>
                      ) : (
                        getStatusActionLabel(nextStatus)
                      )}
                    </Button>
                  </div>
                )}

                {/* Pick Remaining Items Button - Show for picked/delivered POs with remaining items */}
                {['picked', 'delivered_to_warehouse'].includes(task.pickup_status) && hasRemainingItems(task) && (
                  <div className="px-4 pb-4">
                    <Button
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                      onClick={(e) => handleInitiateRepickup(task, e)}
                      disabled={initiatingRepickup === task.id}
                      data-testid={`repickup-btn-${task.id}`}
                    >
                      {initiatingRepickup === task.id ? (
                        <span className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          Initiating...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          Pick Remaining Items ({getRemainingItemsCount(task)} pcs)
                        </span>
                      )}
                    </Button>
                  </div>
                )}

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-wt-border bg-wt-subtle/30 p-4 space-y-4">
                    {/* Quick Actions */}
                    <div className="flex gap-2">
                      {task.vendor_phone && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-wt-border text-wt-text-primary"
                          asChild
                        >
                          <a href={`tel:${task.vendor_phone}`}>
                            <Phone className="h-4 w-4 mr-2" />
                            Call
                          </a>
                        </Button>
                      )}
                      {task.vendor_address && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-wt-border text-wt-text-primary"
                          asChild
                        >
                          <a 
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(task.vendor_address + ', ' + task.vendor_city)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Navigation className="h-4 w-4 mr-2" />
                            Navigate
                          </a>
                        </Button>
                      )}
                    </div>

                    {/* Vendor Contact Card */}
                    <div className="bg-wt-bg rounded-lg p-3 space-y-2">
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-wt-text-muted" />
                        <span className="text-wt-text-muted">Contact:</span>
                        <span className="text-wt-text-primary">{task.vendor_contact_person || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Building2 className="h-4 w-4 text-wt-text-muted" />
                        <span className="text-wt-text-muted">Destination:</span>
                        <span className="text-wt-text-primary">{task.warehouse_location_name || 'Main Warehouse'}</span>
                      </div>
                    </div>

                    {/* Products List - Card Style */}
                    <div>
                      <h4 className="text-wt-text-primary font-medium text-sm mb-2">Products to Pickup</h4>
                      <div className="space-y-2">
                        {task.line_items?.map((item, i) => {
                          const ordered = item.quantity || 0;
                          const picked = item.picked_quantity || 0;
                          const remaining = ordered - picked;
                          const isPartiallyPicked = picked > 0 && picked < ordered;
                          const isFullyPicked = picked >= ordered;
                          
                          return (
                          <div key={i} className={`flex items-center gap-3 bg-wt-bg rounded-lg p-3 ${isPartiallyPicked ? 'border border-orange-500/30' : ''}`}>
                            {item.image_url ? (
                              <ImageLightbox 
                                src={item.image_url} 
                                alt={item.product_title}
                                className="w-12 h-12 flex-shrink-0"
                                thumbnailClassName="w-12 h-12 object-cover rounded"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-wt-subtle rounded flex items-center justify-center flex-shrink-0">
                                <Package className="h-5 w-5 text-wt-text-muted" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-wt-gold font-medium">{item.sku}</p>
                              <p className="text-sm text-wt-text-primary line-clamp-1">{item.product_title}</p>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <span className="text-xs text-wt-text-muted">Qty: {ordered}</span>
                                {picked > 0 && (
                                  <span className={`text-xs ${isFullyPicked ? 'text-green-400' : 'text-orange-400'}`}>
                                    Picked: {picked}
                                  </span>
                                )}
                                {isPartiallyPicked && (
                                  <span className="text-xs text-red-400 font-medium">
                                    Remaining: {remaining}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Vendor Invoice */}
                    {task.vendor_invoice_files?.length > 0 && (
                      <div>
                        <h4 className="text-wt-text-primary font-medium text-sm mb-2">Invoice</h4>
                        <div className="flex flex-wrap gap-2">
                          {task.vendor_invoice_files.map((file, i) => (
                            <a
                              key={i}
                              href={`${process.env.REACT_APP_BACKEND_URL}${file}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 px-3 py-2 bg-wt-bg rounded-lg text-wt-text-primary text-sm"
                            >
                              <FileText className="h-4 w-4 text-wt-gold" />
                              Invoice {i + 1}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* POD Images */}
                    {task.pickup_pod_images?.length > 0 && (
                      <div>
                        <h4 className="text-wt-text-primary font-medium text-sm mb-2">Proof of Pickup</h4>
                        <div className="flex flex-wrap gap-2">
                          {task.pickup_pod_images.map((img, i) => (
                            <a
                              key={i}
                              href={`${process.env.REACT_APP_BACKEND_URL}${img}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-16 h-16 rounded-lg overflow-hidden bg-wt-bg"
                            >
                              <img 
                                src={`${process.env.REACT_APP_BACKEND_URL}${img}`} 
                                alt={`POD ${i + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {tasks.length > 0 && (
        <div className="flex flex-col items-center gap-2 pt-2 pb-6" data-testid="pickup-pagination">
          <p className="text-xs text-wt-text-muted">
            Showing {tasks.length} of {totalMatching} tasks
          </p>
          {hasMore && (
            <Button
              variant="outline"
              className="w-full md:w-64 border-wt-border text-wt-text-primary"
              onClick={() => loadPickupTasks(page + 1)}
              disabled={loadingMore}
              data-testid="pickup-load-more"
            >
              {loadingMore ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-current border-t-transparent"></div>
                  Loading…
                </span>
              ) : (
                `Load More (${totalMatching - tasks.length} remaining)`
              )}
            </Button>
          )}
        </div>
      )}

      {/* POD Upload Dialog (Only for "picked" status) */}
      <Dialog open={podDialogOpen} onOpenChange={(open) => {
        setPodDialogOpen(open);
        if (!open) {
          setIsRepickupMode(false);
        }
      }}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-md mx-4 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Camera className="h-5 w-5 text-wt-gold" />
              {isRepickupMode ? 'Pick Remaining Items' : 'Mark as Picked'}
            </DialogTitle>
            {isRepickupMode && (
              <p className="text-sm text-orange-400 mt-1">
                Picking remaining items from partial pickup
              </p>
            )}
          </DialogHeader>
          
          <div className="space-y-4">
            {/* POD Upload */}
            <div>
              <Label className="text-wt-text-primary text-sm">Upload Proof of Pickup *</Label>
              <div className="mt-2">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePodFileSelect}
                  ref={podInputRef}
                  className="hidden"
                />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePodFileSelect}
                  ref={podGalleryInputRef}
                  className="hidden"
                />
                {podFiles.length > 0 && (
                  <div className="space-y-2 mb-2">
                    {podFiles.map((f, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                        <span className="text-wt-text-primary flex-1 truncate text-sm">{f.name}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setPodFiles(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-400 p-1 h-auto"
                          data-testid={`pickup-pod-remove-${idx}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {podFiles.length < 6 && (
                  <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                    <Button
                      variant="outline"
                      className="w-full border-dashed border-wt-border text-wt-text-muted"
                      onClick={() => podInputRef.current?.click()}
                      data-testid="pickup-pod-camera-btn"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Camera
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full border-dashed border-wt-border text-wt-text-muted"
                      onClick={() => podGalleryInputRef.current?.click()}
                      data-testid="pickup-pod-gallery-btn"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Gallery
                    </Button>
                  </div>
                )}
              </div>
              {!podFiles.length && selectedTask?.pickup_pod_images?.length > 0 && (
                <p className="text-xs text-green-400 mt-2">
                  {selectedTask.pickup_pod_images.length} POD image(s) already uploaded
                </p>
              )}
            </div>

            {/* Items to Pick */}
            {pickedItems.length > 0 && (
              <div className="border border-wt-border rounded-lg overflow-hidden">
                <div className="bg-wt-subtle p-2 text-sm font-medium text-wt-text-primary">
                  Items to Pick
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {pickedItems.map((item, idx) => (
                    <div key={item.line_id} className="p-3 border-t border-wt-border/50">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-wt-gold">{item.sku}</p>
                          <p className="text-sm text-wt-text-primary line-clamp-1">{item.product_title}</p>
                          <p className="text-xs text-wt-text-muted mt-1">
                            Ordered: {item.ordered_quantity} | Remaining: {item.remaining_quantity}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-wt-text-muted">Qty:</Label>
                          <input
                            type="number"
                            min="0"
                            max={item.remaining_quantity}
                            value={item.picked_quantity}
                            onChange={(e) => {
                              const newItems = [...pickedItems];
                              newItems[idx].picked_quantity = Math.min(
                                parseInt(e.target.value) || 0,
                                item.remaining_quantity
                              );
                              setPickedItems(newItems);
                            }}
                            className="w-16 px-2 py-1 text-center bg-wt-bg border border-wt-border rounded text-wt-text-primary text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-wt-subtle p-2 text-xs flex justify-between">
                  <span className="text-wt-text-muted">
                    Total: {pickedItems.reduce((sum, i) => sum + i.remaining_quantity, 0)}
                  </span>
                  <span className="text-wt-gold font-medium">
                    Picking: {pickedItems.reduce((sum, i) => sum + i.picked_quantity, 0)}
                  </span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => { setPodDialogOpen(false); setPodFiles([]); }}
                className="flex-1 border-wt-border text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button 
                onClick={handlePodSubmit}
                disabled={uploadingPod}
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                data-testid="confirm-pickup-btn"
              >
                {uploadingPod ? 'Uploading...' : 'Confirm Pickup'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorPickupsPage;
